<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcMailAccountSelector',
    inheritAttrs: false,
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'input']);

  const props = defineProps({
    item: {
      type: Object,
      required: true,
    },
    path: {
      type: String,
      required: true,
    },
    required: {
      type: Boolean,
      required: false,
    },
    readonly: {
      type: Boolean,
      required: false,
    },
    disabled: {
      type: Boolean,
      required: false,
    },
    autofocus: {
      type: Boolean,
      required: false,
    },
    clearable: {
      type: Boolean,
      required: false,
    },
    bindObject: {
      type: Boolean,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const options = ref(null);
  const ux = ref([]);

  const $data = computed(() => ({
    options: options.value,
    ux: ux.value,
  }));

  const $$load_options = async () => {
    return $app.value.repo.get('list_object_by_type', {
      parameters: { object_type: 'mail-account' },
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'options' }, { name: 'ux' }],
    loaders: { options: $$load_options },
    ux: {},
    dataRefs: { options, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.item,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('item');
    }
  );
  watch(
    () => props.path,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('path');
    }
  );
  watch(
    () => props.required,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('required');
    }
  );
  watch(
    () => props.readonly,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('readonly');
    }
  );
  watch(
    () => props.disabled,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('disabled');
    }
  );
  watch(
    () => props.autofocus,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('autofocus');
    }
  );
  watch(
    () => props.clearable,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('clearable');
    }
  );
  watch(
    () => props.bindObject,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('bindObject');
    }
  );

  const act_7315092382398562_script_c5oqklfis_value_callable = async (
    $event
  ) => {
    return emit('input');
  };
  const act_7315092382398562_script_c5oqklfis_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_7315092382398562_script_c5oqklfis_value_callable($event);
      },
    };
  };

  const act_7315092382398562_script_c5oqklfis = new actionTypes.ScriptAction({
    actionArgs: act_7315092382398562_script_c5oqklfis_getActionArgs,
  });
  const inst_evh_7315092382398562_input_wzkbjlxb6 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_script_c5oqklfis,
      event: 'input',
      displayName: 'script',
    });
  const inst_evh_7315092382398562_input = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_input_wzkbjlxb6],
    event: 'input',
  });

  const actions = {
    inst_evh_7315092382398562_input_wzkbjlxb6,
    inst_evh_7315092382398562_input,
  };

  const evh_7315092382398562_input_wzkbjlxb6 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_input_wzkbjlxb6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_input = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_input.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpFormSelect
    :name="path"
    v-model="item[path]"
    :value="item[path]"
    @input="
      !($event && typeof $event.stopPropagation === 'function') &&
        (item[path] =
          $event && typeof $event === 'object' && 'data' in $event
            ? $event.data
            : $event);
      evh_7315092382398562_input($event, {});
    "
    :options="options"
    prefetch
    valueField="id"
    textField="description,address,server"
    :bindObject="bindObject"
    :clearable="clearable"
    :autofocus="autofocus"
    :disabled="disabled || readonly || item.isReadonly"
    :readonly="readonly || item.isReadonly"
    :required="required"
    v-if="item"
  >
  </ItpFormSelect>
</template>
